import { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { coursesApi } from '@/features/courses/api/courses.api'
import { sessionsApi } from '@/features/sessions/api/sessions.api'
import { useSessionStore } from '@/features/sessions/store/sessionStore'

/** Clés i18n des suggestions proposées sur l'écran d'accueil du chat. */
const SUGGESTION_KEYS = [
  'chat.suggestions.photosynthesis',
  'chat.suggestions.neural_networks',
  'chat.suggestions.derivative',
  'chat.suggestions.french_revolution',
  'chat.suggestions.spanish',
  'chat.suggestions.quantum_physics',
] as const

function makeTitle(text: string): string {
  const clean = text.trim().replace(/\s+/g, ' ')
  return clean.length > 80 ? clean.slice(0, 77).trimEnd() + '...' : clean
}

export function ChatPage() {
  const { t } = useTranslation()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setCurrentSession, setPendingFirstMessage } = useSessionStore()

  function autoResize() {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }

  // Le texte est passé en argument : la fonction ne lit aucune ref, ce qui la
  // rend mémoïsable et testable indépendamment du DOM.
  const startSession = useCallback(async (rawText: string) => {
    const text = rawText.trim()
    if (!text || isStarting) return

    setIsStarting(true)
    setError(null)

    try {
      const courseRes = await coursesApi.create({
        title: makeTitle(text),
        mode: 'free',
        language: 'fr',
      })
      const courseId = courseRes.data.data.id

      await coursesApi.publish(courseId)
      queryClient.invalidateQueries({ queryKey: ['courses'] }).catch(() => undefined)

      const sessionRes = await sessionsApi.start(courseId)
      const session = sessionRes.data.data

      setCurrentSession(session)
      setPendingFirstMessage(text)
      // Store sessionId so ChatHistorySidebar can navigate directly to this session
      localStorage.setItem(`mindeia:session:${courseId}`, session.id)
      navigate(`/courses/${courseId}/sessions/${session.id}`, { replace: true })
    } catch {
      setError(t('chat.start_error'))
      setIsStarting(false)
    }
  }, [isStarting, navigate, setCurrentSession, setPendingFirstMessage, queryClient, t])

  function submitCurrentInput() {
    startSession(textareaRef.current?.value ?? '').catch(() => undefined)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submitCurrentInput()
    }
  }

  function handleSuggestionClick(text: string) {
    if (textareaRef.current) {
      textareaRef.current.value = text
      autoResize()
    }
    startSession(text).catch(() => undefined)
  }

  return (
    <div className="flex flex-col h-full bg-surface overflow-hidden">
      {/* Centered greeting + suggestions */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-4 overflow-y-auto">
        <div className="w-full max-w-xl">
          <h1 className="text-2xl font-bold text-on-surface mb-1.5 text-center font-display">
            {t('chat.heading')}
          </h1>
          <p className="text-sm text-outline text-center mb-8">
            {t('chat.subheading')}
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {SUGGESTION_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => handleSuggestionClick(t(key))}
                disabled={isStarting}
                className="px-4 py-2 rounded-full bg-surface-low border border-outline-variant text-sm text-on-surface-variant hover:border-primary hover:text-primary hover:bg-primary/5 transition-all disabled:opacity-50"
              >
                {t(key)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom input bar */}
      <div className="px-4 pb-[calc(1.5rem+var(--safe-area-inset-bottom))] pt-3 bg-gradient-to-t from-surface via-surface to-transparent shrink-0">
        {error && (
          <p className="text-xs text-error mb-2 text-center">{error}</p>
        )}
        <div className="max-w-2xl mx-auto">
          <div className="flex items-end gap-3 bg-white border border-outline-variant rounded-2xl px-4 py-3 shadow-sm focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-all">
            <textarea
              ref={textareaRef}
              rows={1}
              disabled={isStarting}
              placeholder={t('chat.placeholder')}
              onChange={autoResize}
              onKeyDown={handleKeyDown}
              className="flex-1 resize-none bg-transparent text-sm text-on-surface placeholder-outline focus:outline-none disabled:opacity-50 leading-relaxed min-h-[44px]"
              autoFocus
            />
            <button
              type="button"
              onClick={submitCurrentInput}
              disabled={isStarting}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary hover:opacity-90 text-white transition-opacity disabled:opacity-50 shrink-0 mb-0.5"
              aria-label={t('chat.send')}
            >
              {isStarting ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.269 20.876L5.999 12zm0 0h7.5" />
                </svg>
              )}
            </button>
          </div>
          <p className="text-center text-xs text-outline/60 mt-2">
            {t('session.enter_hint')}
          </p>
        </div>
      </div>
    </div>
  )
}
